<template>
  <div class="page">
    <van-nav-bar title="操作合约" />

    <w3m-button size="sm" label="连接" balance="hide" />

    <van-loading v-if="isLoading" type="spinner" color="#1989fa" />

    <template v-else>
      <van-cell-group>
        <van-cell title="当前合约地址">
          <template #default>{{ contractAddress }}</template>
        </van-cell>

        <van-cell title="合约所有者" :value="owner" />
        <van-cell title="代币余额" :value="balance" />
        <van-cell title="当前账户">
          <template #default>{{ account.address }}</template>
        </van-cell>
      </van-cell-group>

      <van-divider>接收分红</van-divider>
      <van-cell-group>
        <van-cell title="当前钱包 USDT 余额" :value="usdtBalance" />
        <van-field v-model="dividendsAmount" label="输入要转入的 USDT 数量" />
        <van-cell title="当前授权 USDT 数量" :value="formattedUsdtAllowance" />
        <van-button v-if="!isApproved" block @click="approveUsdt" type="primary">授权</van-button>
        <van-button v-if="isApproved" block @click="authorizeAndReceiveDividends" type="primary">
          转入利润
        </van-button>
      </van-cell-group>

      <van-divider>转换合约所有者</van-divider>
      <van-cell-group>
        <van-field v-model="newOwner" label="输入新所有者地址" />
        <van-button @click="changeOwner" block type="primary">转换所有者</van-button>
      </van-cell-group>

      <van-divider>设置代币价格</van-divider>
      <van-cell-group>
        <van-cell title="当前代币价格" :value="currentTokenPrice + ' USDT'" />
        <van-field v-model="newTokenPrice" label="输入新的代币价格" />
        <van-button @click="updateTokenPrice" block type="primary">设置价格</van-button>
      </van-cell-group>

      <van-divider>设置投资接收地址</van-divider>
      <van-cell-group>
        <van-cell title="当前投资接收地址" :value="currentInvestmentReceiver" />
        <van-field v-model="newInvestmentReceiver" label="输入新的接收地址" />
        <van-button @click="updateInvestmentReceiver" block type="primary">设置地址</van-button>
      </van-cell-group>

      <van-divider>将收到的分红，分配到帐户，可以在后台执行</van-divider>
      <van-cell-group>
        <van-cell title="当前未分红的数量" :value="undistributedDividends" />
        <van-button @click="distributeDividends" block type="primary">计算分红</van-button>
      </van-cell-group>

      <van-divider>设定 USDT 地址</van-divider>
      <van-cell-group>
        <van-cell title="当前 USDT 地址" :value="currentUsdtAddress" />
        <van-field v-model="newUsdtAddress" label="输入新的 USDT 地址" />
        <van-button @click="setUsdAddress" block type="primary">设置 USDT 地址</van-button>
      </van-cell-group>

      <van-divider>加入黑名单</van-divider>
      <van-cell-group>
        <van-field v-model="blacklistAddress" label="输入要加入黑名单的地址" />
        <van-button @click="addToBlacklist" block type="primary">加入黑名单</van-button>
      </van-cell-group>

      <van-divider>移除黑名单</van-divider>
      <van-cell-group>
        <van-field v-model="removeBlacklistAddress" label="输入要移除黑名单的地址" />
        <van-button @click="removeFromBlacklist" block type="primary">移除黑名单</van-button>
      </van-cell-group>

      <van-divider>铸造代币</van-divider>
      <van-cell-group>
        <van-field v-model="mintAddress" label="输入铸造代币的地址" />
        <van-field v-model="mintAmount" label="输入铸造代币的数量" />
        <van-button @click="mintTokens" block type="primary">铸造代币</van-button>
      </van-cell-group>

      <van-divider>提取合约中的利润</van-divider>
      <van-cell-group>
        <van-field v-model="dividendAmount" label="输入要提取的分红数量" />
        <van-button @click="extractDividends" block type="primary">提取分红</van-button>
      </van-cell-group>

      <van-divider>提取多余的 USDT</van-divider>
      <van-cell-group>
        <van-field v-model="excessAmount" label="输入要提取的 USDT 数量" />
        <van-button @click="extractExcessUSDT" block type="primary">提取多余的 USDT</van-button>
      </van-cell-group>

      <van-divider>更新提取等待时间</van-divider>
      <van-cell-group>
        <van-field v-model="newClaimWait" label="输入新的等待时间 (秒)" />
        <van-button @click="updateClaimWait" block type="primary">更新等待时间</van-button>
      </van-cell-group>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watchEffect } from "vue";
import Swal from "sweetalert2";

import { config } from "../composables/walletConfig";

import { getAccount, readContract, writeContract } from '@wagmi/core'
import { abi } from "../composables/pcd_abi";
import utils from '../composables/utils';

const { convertToWei, convertFromWei } = utils;
const addresses = require('../composables/addresses');


const USDT_DECIMALS = 6;

const isLoading = ref(true);
const owner = ref("");
const balance = ref(0);
const newTokenPrice = ref("");
const newInvestmentReceiver = ref("");
const currentTokenPrice = ref("");
const currentInvestmentReceiver = ref("");
const currentUsdtAddress = ref("");
const contractAddress = ref(addresses.contractAddress);
const usdtAddress = ref("0xc2132d05d31c914a87c6611c10748aeb04b58e8f");
const newOwner = ref("");
const newUsdtAddress = ref("");
const blacklistAddress = ref("");
const removeBlacklistAddress = ref("");
const dividendAmount = ref("");
const excessAmount = ref("");
const newClaimWait = ref("");
const dividendsAmount = ref("");
const usdtBalance = ref("");
const usdtAllowance = ref(0);
const mintAddress = ref("");
const mintAmount = ref("");
const undistributedDividends = ref(0);
const isApproved = ref(false);

const formattedUsdtAllowance = computed(() => {
  return convertFromWei(usdtAllowance.value.toString(), USDT_DECIMALS, 2);
});

onMounted(async () => {
  await updateAllData();
});

watchEffect(() => {
  const account = getAccount(config)
  if (account.isConnected) {
    updateAllData();
  } else {
    // 清除所有数据
    balance.value = 0;
    usdtBalance.value = "";
    usdtAllowance.value = 0;
    owner.value = "";
    currentTokenPrice.value = "";
    currentInvestmentReceiver.value = "";
    undistributedDividends.value = 0;
  }
});

const updateAllData = async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      getUsdtBalance(),
      getUndistributedDividends(),
      getUsdtAllowance(),
      getOwner(),
      getTokenPrice(),
      getInvestmentReceiver(),
    ]);
  } catch (error) {
    console.error("更新��据时发生错误:", error);
    Swal.fire({
      title: "错误",
      text: "更新数据时发生错误，请重试",
      icon: "error",
      confirmButtonText: "确定",
    });
  } finally {
    isLoading.value = false;
  }
};

const getUsdtBalance = async () => {
  try {
    const account = getAccount()
    if (account.address) {
      const balance = await readContract(config,{
        abi,
        address: usdtAddress.value,
        functionName: "balanceOf",
        args: [account.address],
      });
      console.log("USDT balance:", balance);

      usdtBalance.value = convertFromWei(balance, USDT_DECIMALS, 2);
    }
  } catch (error) {
    console.error("获取 USDT 余额时发生错误:", error);
    throw error;
  }
};

const getUsdtAllowance = async () => {
  try {
    const account = getAccount(config)
    if (account.address) {
      const allowanceResult = await readContract(config,{
        abi,
        address: usdtAddress.value,
        functionName: 'allowance',
        args: [account.address, contractAddress.value],
      });

      usdtAllowance.value = allowanceResult;
      console.log("USDT 授权数量:", convertFromWei(allowanceResult.toString(), USDT_DECIMALS, 2));
    } else {
      console.error("当前账户未初始化");
    }
  } catch (error) {
    console.error("获取 USDT 授权数量时发生错误:", error);
  }
};

const getOwner = async () => {
  try {
    const result = await readContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'owner',
      args: [],
    });

    owner.value = result;
    console.log("合约所有者:", result);
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "获取合约所有者时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
    console.error("错误详细信息:", error);
  }
};

const mintTokens = async () => {
  try {
    const account = getAccount(config)
    if (account.address) {
      console.log("铸造地址:", mintAddress.value);
      console.log("铸造数量:", mintAmount.value);
      const mintAmountWei = convertToWei(mintAmount.value);

      const mintResult = await writeContract({
        abi,
        address: contractAddress.value,
        functionName: 'mintTokens',
        args: [mintAddress.value, mintAmountWei.toString()],
      });

      console.log("铸造结果:", mintResult);

      Swal.fire({
        title: "成功",
        text: "代币已铸造",
        icon: "success",
        confirmButtonText: "确定",
      });
    } else {
      Swal.fire({
        title: "错误",
        text: "合约未初始化或钱包未连接",
        icon: "error",
        confirmButtonText: "确定",
      });
    }
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "铸造代币时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const updateTokenPrice = async () => {
  try {
    const priceInWei = convertToWei(newTokenPrice.value, USDT_DECIMALS);
    const result = await writeContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'setTokenPrice',
      args: [priceInWei],
    });

    console.log("代币价格已更新:", result);

    Swal.fire({
      title: "成功",
      text: "代币价格已更新",
      icon: "success",
      confirmButtonText: "确定",
    });
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "设置代币价格时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const updateInvestmentReceiver = async () => {
  try {
    const result = await writeContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'setInvestmentReceiver',
      args: [newInvestmentReceiver.value],
    });

    console.log("投资接收地址已更新:", result);

    Swal.fire({
      title: "成功",
      text: "投资接收地址已更新",
      icon: "success",
      confirmButtonText: "确定",
    });
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "设置投资接收地址时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const distributeDividends = async () => {
  try {
    const result = await writeContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'distributeUndistributedDividends',
    });

    console.log("未分配的分红已分配"), result;

    Swal.fire({
      title: "成功",
      text: "未分配的分红已分配",
      icon: "success",
      confirmButtonText: "确定",
    });
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "分配未分配的分红时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const setUsdAddress = async () => {
  try {
    const result = await writeContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'setUsdAddress',
      args: [newUsdtAddress.value],
    });

    console.log("USDT 地址已更新:", result);

    Swal.fire({
      title: "成功",
      text: "USDT 地址已更新",
      icon: "success",
      confirmButtonText: "确定",
    });
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "设置 USDT 地址时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const addToBlacklist = async () => {
  try {
    const result = await writeContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'addToBlacklist',
      args: [blacklistAddress.value],
    });

    console.log("地址已加入黑名单:", result);

    Swal.fire({
      title: "成功",
      text: "地址已加入黑名单",
      icon: "success",
      confirmButtonText: "确定",
    });
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "加入黑名单时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const removeFromBlacklist = async () => {
  try {
    const result = await writeContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'removeFromBlacklist',
      args: [removeBlacklistAddress.value],
    });

    console.log("地址已移除黑名单:", result);

    Swal.fire({
      title: "成功",
      text: "地址已移除黑名单",
      icon: "success",
      confirmButtonText: "确定",
    });
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "移除黑名单时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const extractDividends = async () => {
  try {
    const usdtAmount = convertToWei(dividendAmount.value, USDT_DECIMALS);

    const result = await writeContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'extractDividends',
      args: [usdtAmount.toString()],
    });

    console.log("已提取分红:", result);

    Swal.fire({
      title: "成功",
      text: "已提取分红: " + dividendAmount.value,
      icon: "success",
      confirmButtonText: "确定",
    });
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "提取分红时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const extractExcessUSDT = async () => {
  try {
    const usdtAmount = convertToWei(excessAmount.value, USDT_DECIMALS);

    const result = await writeContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'extractExcessUSDT',
      args: [usdtAmount.toString()],
    });

    console.log("已提取多余的 USDT:", result);

    Swal.fire({
      title: "成功",
      text: "已提取多余的 USDT: " + excessAmount.value,
      icon: "success",
      confirmButtonText: "确定",
    });
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "提取多余的 USDT 时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const updateClaimWait = async () => {
  try {
    const result = await writeContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'updateClaimWait',
      args: [newClaimWait.value],
    });

    console.log("提取等待时间已更新:", result);

    Swal.fire({
      title: "成功",
      text: "提取等待时间已更新: " + newClaimWait.value,
      icon: "success",
      confirmButtonText: "确定",
    });
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "更新提取等待时间时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const getTokenPrice = async () => {
  try {
    const result = await readContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'tokenPrice',
      args: [],
    });
    console.log("当前代币价格:", result);

    currentTokenPrice.value = convertFromWei(result, USDT_DECIMALS, 2);

  } catch (error) {
    console.error("获取代币价格时发生错误:", error);
  }
};

const getInvestmentReceiver = async () => {
  try {
    const result = await readContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'investmentReceiver',
      args: [],
    });

    currentInvestmentReceiver.value = result;
  } catch (error) {
    console.error("获取投资接收地址时发生错误:", error);
  }
};

const changeOwner = async () => {
  try {
    const account = getAccount(config)
    if (newOwner.value && account.address) {
      const result = await writeContract(config,{
        abi,
        address: contractAddress.value,
        functionName: 'transferOwnership',
        args: [newOwner.value],
      });

      console.log("所有者已转换", result);

      Swal.fire({
        title: "成功",
        text: "所有者已转换",
        icon: "success",
        confirmButtonText: "确定",
      });
    } else {
      Swal.fire({
        title: "错误",
        text: "合约未初始化或新所有者地址为空",
        icon: "error",
        confirmButtonText: "确定",
      });
    }
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "转换所有者时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const authorizeAndReceiveDividends = async () => {
  try {
    const account = getAccount(config)
    if (account.address) {
      const usdtAmount = convertToWei(dividendsAmount.value, USDT_DECIMALS);

      if (usdtAllowance.value < usdtAmount) {
        Swal.fire({
          title: "错误",
          text: "USDT 授权数量不足，请先授权。",
          icon: "error",
          confirmButtonText: "确定",
        });
        return;
      }

      const result = await writeContract(config,{
        abi,
        address: contractAddress.value,
        functionName: 'receiveDividends',
        args: [usdtAmount.toString()],
      });

      console.log("利润已转入合约", result);

      Swal.fire({
        title: "成功",
        text: "利润已转入合约",
        icon: "success",
        confirmButtonText: "确定",
      });

    } else {
      Swal.fire({
        title: "错误",
        text: "合约未初始化或钱包未连接",
        icon: "error",
        confirmButtonText: "确定",
      });
    }
  } catch (error) {
    Swal.fire({
      title: "错误",
      text: "转入利润时发生错误: " + error.message,
      icon: "error",
      confirmButtonText: "确定",
    });
  }
};

const getUndistributedDividends = async () => {
  try {
    const result = await readContract(config,{
      abi,
      address: contractAddress.value,
      functionName: 'undistributedDividends',
      args: [],
    });
    console.log("未分红数量:", result);

    const undistributedDividendsUSDT = convertFromWei(result, USDT_DECIMALS, 2);

    undistributedDividends.value = undistributedDividendsUSDT;
  } catch (error) {
    console.error("获取未分红数量时发生错误:", error);
  }
};

const checkApproval = () => {
  isApproved.value = usdtAllowance.value >= convertToWei(dividendsAmount.value, USDT_DECIMALS);
};

watchEffect(() => {
  checkApproval();
});

// 授权 USDT
const approveUsdt = async () => {
  try {
    const account = getAccount(config)
    if (account.address) {
      if (dividendsAmount.value > 0) {
        const usdtAmount = convertToWei(dividendsAmount.value, USDT_DECIMALS);

        const result = await writeContract(config,{
          abi,
          address: usdtAddress.value,
          functionName: 'approve',
          args: [contractAddress.value, usdtAmount],
        });

        console.log("Approval result:", result);

        Swal.fire({
          title: "成功",
          text: "USDT 授权成功",
          icon: "success",
          confirmButtonText: "确定",
        });

      } else {
        Swal.fire({
          title: "错误",
          text: "请输入需要投资的数量",
          icon: "warning",
          confirmButtonText: "确定",
        });
      }
    }
  } catch (error) {
    if (error.code === 4001) {
      Swal.fire({
        title: "授权失败",
        text: "授权失败: " + error.message,
        icon: "error",
        confirmButtonText: "确定",
      });
    } else {
      Swal.fire({
        title: "错误",
        text: "授权时发生错误: " + error.message,
        icon: "error",
        confirmButtonText: "确定",
      });
    }
  }
};
</script>

<style scoped>
.page {
  background-color: #eeeeee;
}
</style>

